/* screens/settings/TeamTab.jsx — команда (window.TeamTab).
   Участники (MemberRow) + приглашения (InvitationRow) + роли доступа (T3, RoleEditorModal)
   + InviteMemberModal/EditMemberModal (роли пропсом сверху — модалки сами не фетчат).
   403-паттерн ForbiddenState при отсутствии прав (не пустой список). Все удаления —
   ConfirmDialog (не window.confirm). // источник: 11 §2.9.
   Данные — teamReal (workspace-scoped, wsId = activeWorkspace.id из useAuth); бэк 403 не-админу
   → QueryBoundary.error. getTeam отдаёт и roles (ручка /roles/ той же волны; до бэка — []).
   ХЕНДОФФ → frontend/pages/settings/TeamTab.jsx. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { EditMemberModal } from '../../components/patterns/EditMemberModal.jsx';
import { InvitationRow } from '../../components/patterns/InvitationRow.jsx';
import { InviteMemberModal } from '../../components/patterns/InviteMemberModal.jsx';
import { MemberRow } from '../../components/patterns/MemberRow.jsx';
import { RoleEditorModal } from '../../components/patterns/RoleEditorModal.jsx';
import { QueryBoundary } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { hooks } from '../../hooks/index.jsx';
import { teamReal } from '../../lib/api/index.js';
import { rolesApplicable } from '../../lib/planSections.js';

  const { useState } = React;
  const { useFakeQuery } = hooks;

  // Ярлык статуса без роли доступа (зеркалит адаптер) — для локального пересчёта
  // role_name участников после переименования/удаления роли.
  const statusLabel = (m) => (m.is_owner ? 'Владелец' : m.is_admin ? 'Администратор' : 'Участник');

  function Inner({ data, wsId }) {
    const toast = useToast();
    const confirm = useConfirm();
    const [members, setMembers] = useState(data.members);
    const [invites, setInvites] = useState(data.invitations);
    // roles=null — маркер getTeam «ручка ролей сбоит (не 404)»: секция «Роли» кажет
    // нейтральный текст вместо ложного «Ролей пока нет»; локальный стейт держим массивом.
    const rolesUnavailable = data.roles === null;
    const [roles, setRoles] = useState(data.roles || []);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editMember, setEditMember] = useState(null);
    const [roleModal, setRoleModal] = useState(null); // null | {role: null|role} (создание|правка)
    const canManage = data.can_manage;
    // Роли ограничивают вкладки аналитики кассы. Если тариф пространства закрыл «Аналитику» или
    // «Интеграции», ограничивать нечего — секцию «Роли» и выбор роли в модалках не показываем:
    // владелец такой команды не должен видеть ни настроек доступа к аналитике, ни её упоминаний.
    const { workspaces } = useAuth();
    const workspace = (workspaces || []).find((w) => w.id === wsId) || null;
    const showRoles = rolesApplicable(workspace);

    const onInvite = async (payload) => {
      const inv = await teamReal.inviteMember(wsId, payload);
      setInvites((list) => [inv, ...list]);
      // Явно закрываем модалку (не полагаемся только на self-close): онбординг
      // на следующем шаге подсвечивает вкладку ПОД этой модалкой.
      setInviteOpen(false);
      toast.show('Приглашение отправлено на ' + inv.email, 'success');
      // Сигнал оверлею онбординга — на СЛЕДУЮЩЕМ тике, чтобы React успел закрыть
      // модалку до продвижения шага (иначе цель нового шага остаётся за окном).
      setTimeout(() => {
        try { window.dispatchEvent(new CustomEvent('onb:invited')); } catch { /* noop */ }
      }, 0);
    };
    const onSaveMember = async (member, patch) => {
      // Повышение до admin может только owner ws (иначе бэк 403 forbidden); даём понятный тост.
      try {
        const r = await teamReal.updateMember(wsId, member.id, patch);
        setMembers((list) => list.map((m) => (m.id === member.id ? { ...m, ...r } : m)));
        // members_count у ролей — локально: старая роль −1, новая +1.
        const oldRoleId = member.assigned_role ? member.assigned_role.id : null;
        const newRoleId = r.assigned_role ? r.assigned_role.id : null;
        if (oldRoleId !== newRoleId) {
          setRoles((list) => list.map((role) => (
            role.id === oldRoleId ? { ...role, members_count: Math.max(0, role.members_count - 1) }
            : role.id === newRoleId ? { ...role, members_count: role.members_count + 1 }
            : role
          )));
        }
        toast.show('Участник обновлён', 'success');
      } catch (e) {
        toast.show(e.code === 'forbidden' ? 'Назначать администраторов может только владелец'
          : e.code === 'invalid_role' ? 'Роль не найдена — обновите страницу'
          : 'Не удалось обновить участника', 'danger');
        // Пробрасываем reject в EditMemberModal: при ошибке PATCH модалка НЕ
        // закрывается — набранная матрица исключений сохраняется (T5 m4).
        throw e;
      }
    };
    const onRevokeMember = async (member) => {
      const ok = await confirm({ title: 'Исключить участника?', body: '«' + member.full_name + '» потеряет доступ к пространству.', confirmLabel: 'Исключить', cancelLabel: 'Отмена', tone: 'danger' });
      if (!ok) return;
      try {
        await teamReal.revokeMember(wsId, member.id);
        setMembers((list) => list.filter((m) => m.id !== member.id));
        if (member.assigned_role) {
          setRoles((list) => list.map((r) => (r.id === member.assigned_role.id ? { ...r, members_count: Math.max(0, r.members_count - 1) } : r)));
        }
        toast.show('Участник исключён', 'muted');
      } catch (e) {
        toast.show(e.code === 'cannot_remove_owner' ? 'Нельзя исключить владельца' : 'Не удалось исключить участника', 'danger');
      }
    };
    const onResendInvite = async (inv) => {
      try {
        const updated = await teamReal.resendInvitation(wsId, inv.id);
        setInvites((list) => list.map((i) => (i.id === inv.id ? updated : i)));
        toast.show('Приглашение отправлено повторно на ' + inv.email, 'success');
      } catch (e) {
        const msg = e.code === 'rate_limited' ? 'Слишком часто, попробуйте позже'
          : e.code === 'already_accepted' ? 'Приглашение уже принято'
          : e.code === 'invitation_revoked' ? 'Приглашение отозвано'
          : 'Не удалось отправить повторно';
        toast.show(msg, 'danger');
      }
    };
    const onRevokeInvite = async (inv) => {
      const ok = await confirm({ title: 'Отозвать приглашение?', body: 'Ссылка для ' + inv.email + ' перестанет действовать.', confirmLabel: 'Отозвать', cancelLabel: 'Отмена', tone: 'danger' });
      if (!ok) return;
      try {
        await teamReal.revokeInvitation(wsId, inv.id);
        setInvites((list) => list.filter((i) => i.id !== inv.id));
        toast.show('Приглашение отозвано', 'muted');
      } catch (e) {
        toast.show('Не удалось отозвать приглашение', 'danger');
      }
    };

    // ── Роли доступа ── (ошибки invalid_name/invalid_grants ловит и показывает RoleEditorModal)
    const onSaveRole = async (role, payload) => {
      if (!role) {
        const r = await teamReal.createRole(wsId, payload);
        setRoles((list) => [...list, r]);
        toast.show('Роль «' + r.name + '» создана', 'success');
        return;
      }
      const r = await teamReal.updateRole(wsId, role.id, payload);
      setRoles((list) => list.map((x) => (x.id === r.id ? r : x)));
      // имя роли живёт и в бейджах участников/приглашений — синкаем локально.
      setMembers((list) => list.map((m) => (
        m.assigned_role && m.assigned_role.id === r.id
          ? { ...m, assigned_role: { id: r.id, name: r.name }, role_name: (!m.is_admin && !m.is_owner) ? r.name : m.role_name }
          : m
      )));
      setInvites((list) => list.map((i) => (
        i.assigned_role && i.assigned_role.id === r.id
          ? { ...i, assigned_role: { id: r.id, name: r.name }, role_name: !i.is_admin ? r.name : i.role_name }
          : i
      )));
      toast.show('Роль обновлена', 'success');
    };
    const onDeleteRole = async (role) => {
      const ok = await confirm({
        title: 'Удалить роль «' + role.name + '»?',
        body: 'Участники с этой ролью останутся без роли и получат полный доступ.',
        confirmLabel: 'Удалить', cancelLabel: 'Отмена', tone: 'danger',
      });
      if (!ok) return;
      try {
        await teamReal.deleteRole(wsId, role.id);
        setRoles((list) => list.filter((r) => r.id !== role.id));
        setMembers((list) => list.map((m) => (
          m.assigned_role && m.assigned_role.id === role.id
            ? { ...m, assigned_role: null, role_name: statusLabel(m) }
            : m
        )));
        setInvites((list) => list.map((i) => (
          i.assigned_role && i.assigned_role.id === role.id
            ? { ...i, assigned_role: null, role_name: i.is_admin ? 'Администратор' : 'Участник' }
            : i
        )));
        toast.show('Роль удалена', 'muted');
      } catch (e) {
        toast.show('Не удалось удалить роль', 'danger');
      }
    };

    const restrictionNote = (r) => (r.restricted_count > 0 ? 'Ограничений: ' + r.restricted_count : 'Полный доступ');

    return (
      <div className="team stack stack-24">
        <section className="bill-section">
          <div className="bill-section__head">
            <h3 className="t-h4">Участники <span className="num text-mute">({members.length})</span></h3>
            {canManage && <Button variant="primary" size="sm" iconLeft="user-add" data-onb="team-invite" onClick={() => setInviteOpen(true)}>Пригласить</Button>}
          </div>
          <div className="team-list">
            {members.map((m) => <MemberRow key={m.id} member={m} canManage={canManage} onEdit={setEditMember} onRevoke={onRevokeMember} />)}
          </div>
        </section>

        <section className="bill-section">
          <div className="bill-section__head"><h3 className="t-h4">Приглашения <span className="num text-mute">({invites.length})</span></h3></div>
          {invites.length === 0 ? (
            <div className="team-empty t-body-sm text-mute">Активных приглашений нет</div>
          ) : (
            <div className="team-list">
              {invites.map((i) => <InvitationRow key={i.id} invitation={i} canManage={canManage} onRevoke={onRevokeInvite} onResend={onResendInvite} />)}
            </div>
          )}
        </section>

        {showRoles && (
          <section className="bill-section">
            <div className="bill-section__head">
              <h3 className="t-h4">Роли {!rolesUnavailable && <span className="num text-mute">({roles.length})</span>}</h3>
              {canManage && !rolesUnavailable && <Button variant="secondary" size="sm" iconLeft="plus" onClick={() => setRoleModal({ role: null })}>Создать роль</Button>}
            </div>
            {rolesUnavailable ? (
              <div className="team-empty t-body-sm text-mute">Роли временно недоступны — обновите страницу или попробуйте позже.</div>
            ) : roles.length === 0 ? (
              <div className="team-empty t-body-sm text-mute">Ролей пока нет — участники без роли видят всю аналитику. Создайте роль, чтобы ограничить доступ.</div>
            ) : (
              <div className="team-list">
                {roles.map((r) => (
                  <div className="team-row" key={r.id}>
                    <span className="member-avatar member-avatar--ghost" aria-hidden="true"><Icon name="shield" size={16} /></span>
                    <div className="team-row__id">
                      <div className="team-row__name t-label">{r.name}</div>
                      <div className="team-row__email t-caption text-dim">{'Участников: ' + r.members_count + ' · ' + restrictionNote(r)}</div>
                    </div>
                    {canManage && (
                      <div className="team-row__actions cluster cluster-4">
                        <Button variant="ghost" size="sm" iconLeft="edit" onClick={() => setRoleModal({ role: r })}>Изменить</Button>
                        <Button variant="ghost" size="sm" iconLeft="trash" onClick={() => onDeleteRole(r)}>Удалить</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <InviteMemberModal open={inviteOpen} roles={showRoles ? roles : []} onClose={() => setInviteOpen(false)} onInvite={onInvite} />
        <EditMemberModal open={!!editMember} member={editMember} roles={showRoles ? roles : []} onClose={() => setEditMember(null)} onSave={onSaveMember} />
        <RoleEditorModal open={!!roleModal} role={roleModal ? roleModal.role : null} onClose={() => setRoleModal(null)} onSave={onSaveRole} />
      </div>
    );
  }

  function TeamTab({ wsId: wsIdProp }) {
    // Команда строго workspace-scoped. Приоритет — явный wsId от BusinessSection (выбранный
    // бизнес-ws); фолбэк — активное пространство сессии из useAuth. (BusinessSection пока на
    // моке getBusinessWorkspaces, его id могут не совпасть с боевыми — поэтому фолбэк на active.)
    const { activeWorkspace } = useAuth();
    const wsId = wsIdProp != null ? wsIdProp : (activeWorkspace ? activeWorkspace.id : null);
    const q = useFakeQuery('team:' + wsId, () => teamReal.getTeam(wsId), [wsId]);
    return (
      <QueryBoundary loading={q.loading} error={q.error} onRetry={q.reload}
        skeleton={<div className="stack stack-12">{[0, 1, 2, 3].map((i) => <span key={i} className="skeleton" style={{ height: 60, borderRadius: 'var(--radius-md)' }} />)}</div>}>
        {q.data && <Inner data={q.data} wsId={wsId} />}
      </QueryBoundary>
    );
  }

export { TeamTab };
